import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from '@process-advisor/process-miner/shared/providers/themeProvider';
import { MaterialThemeProvider } from '@process-advisor/process-miner/shared/providers/materialThemeProvider';
import { ColorPickerProvider } from '@process-advisor/process-miner/shared/providers/colorPickerProvider';
import { SidebarLoaderProvider } from '@process-advisor/process-miner/shared/providers/sidebarLoaderProvider';
import { Metricus } from '@process-advisor/process-miner/components/metricus';
import { clearWEBData, setWEBData } from '@process-advisor/process-miner/processMiner/processMiner';
import { modelRepository } from '../services/modelRepository';
import { normalizeRule, normalizeRuleBundle } from '../../../../packages/process-advisor/src/process-miner/rules/ruleSchema';

const SECTION_TO_MODE = {
  evidence: 'metrics',
  explorer: 'process_miner',
  advisor: 'quickHealthcheck',
  value: 'valueSimulation',
  assessment: 'consultingWorkspace',
};

const MODE_TO_SECTION = {
  home: 'evidence',
  metrics: 'evidence', activityMetrics: 'evidence', slaMetrics: 'evidence', segments: 'evidence', variants: 'evidence', transitions: 'evidence', performance_discovery_rules: 'evidence', compliance_discovery_rules: 'evidence', dashboard: 'evidence',
  process_miner: 'explorer', time_in_activity: 'explorer', discovery_rules_visualizer: 'explorer', workflow: 'explorer', cluster_insights: 'explorer', decomposition_tree: 'explorer', influence_analysis: 'explorer', analytics: 'explorer',
  quickHealthcheck: 'advisor', processHealth: 'advisor', trendAnalysis: 'advisor', processInvestigations: 'advisor', aiIntelligence: 'advisor', aiExecutionStrategy: 'advisor', automationPacks: 'advisor', frameworkAssessment: 'advisor', processSnapshot: 'advisor', consultant: 'advisor',
  valueSimulation: 'value',
  consultingWorkspace: 'assessment', consultingReport: 'assessment', workbenchBrief: 'assessment', workbenchFindings: 'assessment', workbenchActions: 'assessment',
};

const unlimitedLocalAccess = Object.freeze({
  plan: 'subscription', pricing: 'subscription', edition: 'advanced', appEdition: 'advanced',
  isSubscription: true, isStandard: false, isAdvanced: true, isLimitedAccess: false,
  isUsageLimited: false, enforceWorkItemLimit: false, workItemLimit: null, taskLimit: null,
  showPlanBadge: false, showLimitLine: false, datasetAiUnlocked: true, aiCreditAuthorized: true,
});

export default function WebProcessAdvisor({ assessment, section = 'evidence', onSectionChange, accessStatus, onActivateAndProcess, onBuyAssessment }) {
  const [upgradeBusy, setUpgradeBusy] = useState(false);
  const modelRulesRef = useRef(null);
  const runtime = assessment?.runtime || assessment?.latestRuntime;
  const dataset = runtime?.canonicalDataset;
  const requestedMode = SECTION_TO_MODE[section] || 'metrics';
  const model = useMemo(() => ({
    ...(dataset?.model || {}),
    id: assessment?.id || dataset?.model?.id || `web-${Date.now()}`,
    name: assessment?.name || dataset?.model?.name || 'Web Model',
    processType: assessment?.processType || dataset?.model?.processType || 'Imported Process',
    process_type: assessment?.processType || dataset?.model?.processType || 'Imported Process',
    category: assessment?.category || dataset?.model?.category || '',
    description: assessment?.description || dataset?.model?.description || '',
    businessObjective: assessment?.businessObjective || assessment?.source?.businessObjective || dataset?.model?.businessObjective || '',
    organizationContext: assessment?.organizationContext || dataset?.model?.organizationContext || {},
    rules: assessment?.rules || dataset?.model?.rules || {version:1,definitions:[],results:[],evaluatedAt:null},
    sourceSystem: assessment?.source?.sourceSystem || dataset?.model?.sourceSystem || '',
    entityLabel: assessment?.source?.entityLabel || dataset?.model?.entityLabel || 'Case',
    activityLabel: assessment?.source?.activityLabel || dataset?.model?.activityLabel || 'Activity',
    attributes: [
      ...(dataset?.attributeDefinitions?.case || []),
      ...(dataset?.attributeDefinitions?.event || []),
    ].map(d => ({
      name:d?.name || d?.sourceField || d?.id,
      sourceField:d?.sourceField || d?.name || d?.id,
      description:d?.description || '',
      type:d?.type || d?.dataType || 'string',
    })).filter(d=>d.name),
    activity_types: [
      assessment?.source?.activityLabel
      || dataset?.model?.activityLabel
      || dataset?.events?.find(e => e?.activityType)?.activityType
      || 'Activity'
    ],
    issue_types: [assessment?.processType || dataset?.model?.processType || 'Imported Process'],
    sample: 'N', category: 'web',
  }), [assessment, dataset]);

  useLayoutEffect(() => {
    document.body.classList.add('pa-web-runtime-active');
    if (dataset) setWEBData(dataset, model);
    if (typeof window!=='undefined') {
      const initialRules=normalizeRuleBundle(model.rules);
      modelRulesRef.current=initialRules;
      window.__PA_WEB_AI_CONTEXT__={
        model:{
          id:model.id,
          name:model.name,
          category:model.category,
          processType:model.processType,
          description:model.description,
          organizationContext:model.organizationContext,
          sourceSystem:model.sourceSystem,
          entityLabel:model.entityLabel,
          activityLabel:model.activityLabel,
          attributes:model.attributes,
          rules:initialRules,
        }
      };
      window.__PA_GET_MODEL_RULES__=()=>normalizeRuleBundle(modelRulesRef.current || model.rules);
      window.__PA_SAVE_MODEL_RULES__=async (rules)=>{
        if(!assessment?.id) return null;
        const normalized=normalizeRuleBundle(rules);
        modelRulesRef.current=normalized;
        if(window.__PA_WEB_AI_CONTEXT__?.model) window.__PA_WEB_AI_CONTEXT__.model.rules=normalized;
        const result=await modelRepository.saveModel({id:assessment.id,rules:normalized});
        return result;
      };
      window.__PA_PROMOTE_DISCOVERY_RULE__=async (rule)=>{
        if(!assessment?.id) throw new Error('This model must be saved before a Discovery Rule can be promoted.');
        const promoted=normalizeRule(rule);
        const current=normalizeRuleBundle(modelRulesRef.current || model.rules);
        const existing=current.definitions.find(r=>r.sourceDiscoveryRuleId && r.sourceDiscoveryRuleId===promoted.sourceDiscoveryRuleId);
        if(existing) return {rule:existing,alreadyPromoted:true};
        const next={...current,definitions:[...current.definitions,promoted],evaluatedAt:null};
        modelRulesRef.current=next;
        if(window.__PA_WEB_AI_CONTEXT__?.model) window.__PA_WEB_AI_CONTEXT__.model.rules=next;
        await modelRepository.saveModel({id:assessment.id,rules:next});
        return {rule:promoted,alreadyPromoted:false};
      };
      window.__PA_SUGGEST_RULES__=async (payload)=>modelRepository.suggestRules({
        ...(payload||{}),
        model:{
          id:model.id,name:model.name,category:assessment?.category || dataset?.model?.category || model.category,processType:model.processType,description:model.description,
          organizationContext:model.organizationContext,sourceSystem:model.sourceSystem,entityLabel:model.entityLabel,
          activityLabel:model.activityLabel,attributes:model.attributes,
        },
      });
    }
    return () => {
      clearWEBData();
      if (typeof window!=='undefined') {
        delete window.__PA_WEB_AI_CONTEXT__;
        delete window.__PA_GET_MODEL_RULES__;
        delete window.__PA_SAVE_MODEL_RULES__;
        delete window.__PA_PROMOTE_DISCOVERY_RULE__;
        delete window.__PA_SUGGEST_RULES__; 
      }
      document.body.classList.remove('pa-web-runtime-active');
    };
  }, [dataset, model]);

  if (!dataset) return <div className="web-pa-empty">This model does not contain a canonical dataset.</div>;

  const processedCount = Number(dataset?.cases?.length || assessment?.summary?.processedCases || assessment?.summary?.cases || 0);
  const configuredCount = Number(
    assessment?.summary?.configuredCases
    || assessment?.summary?.requestedCases
    || dataset?.metadata?.ingestion?.requestedCaseCount
    || dataset?.metadata?.ingestion?.configuredCases
    || processedCount
  );
  const accessSource = String(
    assessment?.accessSource
    || assessment?.entitlement?.source
    || assessment?.summary?.accessSource
    || assessment?.source?.accessSource
    || dataset?.metadata?.ingestion?.accessSource
    || ''
  ).toLowerCase();
  const freeLimited = ['free', 'free_limited', 'free_assessment'].includes(accessSource)
    && configuredCount > processedCount
    && processedCount > 0;
  const handleUpgrade = () => onBuyAssessment?.();

  const handleModeChanged = mode => {
    const nextSection = MODE_TO_SECTION[mode];
    if (nextSection && nextSection !== section) onSectionChange?.(nextSection);
  };

  return (
    <div className="web-process-advisor-host web-process-advisor-embedded">
      {freeLimited && <div className="free-analysis-notice" role="status">
        <span className="free-analysis-notice-copy">
          Showing <b>{processedCount.toLocaleString()}</b> of <b>{configuredCount.toLocaleString()}</b> records on the free allowance.
        </span>
        <button
          type="button"
          className="free-analysis-link"
          disabled={upgradeBusy}
          onClick={handleUpgrade}
        >
          Subscribe for full access
        </button>
      </div>}
      <div className="web-process-advisor-runtime">
      <ThemeProvider><MaterialThemeProvider><ColorPickerProvider><SidebarLoaderProvider>
        <Metricus
          key={`web-pa-${assessment?.id || model.id}`}
          modelID={assessment?.id || model.id}
          refreshData={false}
          activeComponent={() => {}}
          model={model}
          title={model.name}
          license="web-local"
          licenseType="subscription"
          appTitle="Process Advisor"
          AppVersion="WEB"
          contextPath=""
          app="WEB"
          customerID="local-web"
          closePM={() => {}}
          stagilFieldsDC={[]}
          visualizerMode={handleModeChanged}
          customer={{ id: 'local-web', name: 'Local Web' }}
          fields={[]}
          AI={true}
          processLabel={model.processType || "Process"}
          entityLabel={model.entityLabel || "Case"}
          forgeContext={null}
          licenseInfo={{ source: 'web-local', plan: 'subscription' }}
          appEdition="advanced"
          editionAccess={unlimitedLocalAccess}
          requestedMode={requestedMode}
          hidePrimaryHeader={true}
        />
      </SidebarLoaderProvider></ColorPickerProvider></MaterialThemeProvider></ThemeProvider>
      </div>
    </div>
  );
}
